import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function GlasgowPage() {
  return (
    <>
      <LocalBusinessSchema city="Glasgow" latitude={55.8609} longitude={-4.2514} />
      <PageHero
        title="Fractional CISO Services in Glasgow"
        subtitle="Cybersecurity leadership for Scotland's emerging tech powerhouse"
        eyebrow="Glasgow"
        seoTitle="Fractional CISO Glasgow | The CISO Network"
        seoDescription="Expert fractional CISO services for Glasgow businesses. Access senior cybersecurity leadership for Scotland's growing digital economy."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity for Scotland's Innovation Engine
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Glasgow is experiencing a remarkable digital renaissance, transforming from its
                  industrial heritage into one of the UK's most exciting tech destinations. The
                  city's Merchant City innovation district, combined with thriving clusters in
                  fintech, healthtech, and space technology, has created a dynamic environment where
                  traditional industries meet cutting-edge digital innovation.
                </p>
                <p>
                  Glasgow businesses face evolving cybersecurity challenges. The city's burgeoning
                  fintech sector competes on a UK stage and requires robust security programmes.
                  Healthtech innovators working with NHS Scotland must navigate complex data
                  governance requirements. Meanwhile, the established professional services and
                  manufacturing sectors are undergoing digital transformations that require careful
                  security planning.
                </p>
                <p>
                  Many Glasgow businesses are scale-ups spun out of the city's universities or
                  growing SMEs serving UK-wide clients. These businesses need C-level security
                  expertise to win enterprise contracts and satisfy investors, but can't yet justify
                  a six-figure full-time hire. Our fractional CISO service provides Glasgow
                  businesses with the strategic security leadership they need to scale securely.
                </p>
                <p>
                  Glasgow combines a strong public sector presence, energy and utilities
                  infrastructure, and an emerging technology ecosystem. The city's organisations
                  face distinct Scottish regulatory requirements alongside UK-wide obligations. A
                  fractional CISO with Glasgow experience can navigate both Scottish and UK
                  frameworks effectively.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the West of Scotland business environment and
                      its unique security challenges.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Scotland-Wide Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Glasgow, Edinburgh, and throughout Scotland with
                      local CISO expertise.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Glasgow Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Strategic security leadership for Scotland's digital transformation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Scale-Up Security',
                description:
                  'Security programmes that satisfy investors and enterprise clients alike.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description: 'Access senior expertise without the full-time CISO salary overhead.',
              },
              {
                icon: Users,
                title: 'Sector Experience',
                description:
                  'CISOs with Glasgow-relevant experience in fintech, healthtech, and services.',
              },
              {
                icon: Clock,
                title: 'Growth Aligned',
                description:
                  'Flexible engagement that scales with your Glasgow business ambitions.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Glasgow - FAQs"
        subtitle="Common questions from Glasgow and Scottish businesses"
        items={[
          {
            id: 'glasgow-1',
            question: 'How does a fractional CISO work with businesses in Glasgow?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Glasgow. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing public sector, energy, and technology companies.',
          },
          {
            id: 'glasgow-2',
            question: 'What size of business benefits from a fractional CISO in Glasgow?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Glasgow startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'glasgow-3',
            question: 'What industries do your CISOs cover in Glasgow?',
            answer:
              "Our network includes CISOs with experience in public sector, energy, and technology. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'glasgow-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'glasgow-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Glasgow Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can accelerate your growth
            securely.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
